import { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "react-feather";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";
import { responsiveBreakpoints } from "@axvdex/constants";
import { ListItemsProps } from "@axvdex/components/form-element/CustomSelect";
import Button from "@axvdex/components/common/Button";
import CustomInputButton from "@axvdex/components/form-element/CustomInputButton";

interface SolanaSalesBarProps {
  salesList: ListItemsProps[];
  selectedSale?: ListItemsProps;
  setSelectedSale: (props: ListItemsProps | null) => void;
  outbidSaleSelected: string;
}

const SolanaSalesBar = ({ salesList, selectedSale, setSelectedSale, outbidSaleSelected }: SolanaSalesBarProps) => {
  const isMobileBreakpoint = useMediaQuery(responsiveBreakpoints.mobile);
  const isMobileSmBreakpoint = useMediaQuery(responsiveBreakpoints.mobileSm);

  const swiperRef = useRef(null);
  const navigate = useNavigate();
  const [prevState, setPrevState] = useState({ salesList });

  useEffect(() => {
    setPrevState({ salesList });
  }, [salesList]);

  useEffect(() => {
    if (swiperRef && swiperRef.current && salesList.length !== prevState.salesList.length)
      swiperRef.current.swiper.slideTo(salesList.length - 1);
  }, [salesList]);

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Button
        title="Previous"
        btnVariant="icon"
        btnColor="dark-medium"
        extraClassName="swiper-button-prev"
        icon={<ChevronLeft />}
      />

      <Swiper
        ref={swiperRef}
        style={{ border: ".1em solid var(--transparent-white)" }}
        slidesPerView={!isMobileBreakpoint ? 4 : isMobileSmBreakpoint ? 1 : 2}
        slidesPerGroup={1}
        spaceBetween={0}
        centeredSlides={false}
        slidesPerGroupSkip={1}
        modules={[Navigation, Pagination]}
        navigation={{
          prevEl: ".swiper-button-prev",
          nextEl: ".swiper-button-next",
        }}
        pagination={{
          el: ".swiper-pagination",
          clickable: true,
        }}
        onAfterInit={swiper => {
          // Navigate to the end of the Swiper
          swiper.slideTo(salesList.length - 1);
        }}
      >
        {salesList.map(sale => (
          <SwiperSlide key={sale.id} style={{ backgroundColor: "#272727" }}>
            <CustomInputButton
              type="radio"
              id={sale.id}
              name={sale.label}
              labelText={sale.label}
              onClick={() => {
                setSelectedSale(sale);
                navigate(`/outbid/` + outbidSaleSelected + "/" + sale.id);
              }}
              checked={sale.id === selectedSale?.id}
              fullWidth={true}
              onChange={() => null}
              suffixLabel={
                <div style={{ display: "flex", alignItems: "center" }}>
                  {sale.extraFields.withIncentive ? (
                    <span style={{ fontSize: "1.2em", color: "yellow" }}>🏆</span>
                  ) : sale.extraFields.withBid ? (
                    <span style={{ fontSize: "1.2em" }}>🎟</span>
                  ) : null}
                  <span
                    style={{
                      backgroundColor: sale.extraFields.isActive ? "green" : "#ccc",
                      color: sale.extraFields.isActive ? "white" : "black",
                      padding: ".40em",
                      borderRadius: "1em",
                      fontSize: "0.7em",
                      marginLeft: ".5em",
                    }}
                  >
                    {sale.extraFields.isActive
                      ? "live"
                      : sale.extraFields.isEnded
                        ? "ended"
                        : sale.extraFields.isWaiting
                          ? "waiting"
                          : ""}
                  </span>
                </div>
              }
            />
          </SwiperSlide>
        ))}
      </Swiper>

      <Button
        title="Next"
        btnVariant="icon"
        btnColor="dark-medium"
        extraClassName="swiper-button-next"
        icon={<ChevronRight />}
      />
    </div>
  );
};

export default SolanaSalesBar;
